import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Modal from 'react-modal';
import AutoSuggest from 'react-autosuggest';

import { Button, SelectRaw } from '../../../components';
import { short } from '../common/formatting/date';
import {
	getEvisitDetails,
	getReasonsForVisit,
	getPracticePatients,
	getPreRegPatient,
	getLinkedVirtualPractices,
} from './api';
import { formatInTimeZone } from 'date-fns-tz';

const PatientSuggestion = ({
	patient_name_last,
	patient_name_first,
	patient_dob,
	provider_name_first,
	provider_name_last,
	provider_credentials,
}) => (
	<a href="#">
		<strong>
			{patient_name_last}, {patient_name_first}
		</strong>
		<br />
		{short(patient_dob)}
		<br />
		{provider_name_first} {provider_name_last}, {provider_credentials}
	</a>
);
PatientSuggestion.propTypes = {
	patient_name_last: PropTypes.string,
	patient_name_first: PropTypes.string,
	patient_dob: PropTypes.string,
	provider_name_first: PropTypes.string,
	provider_name_last: PropTypes.string,
	provider_credentials: PropTypes.string,
};

export default class EventActionModal extends React.Component {
	static propTypes = {
		isOpen: PropTypes.bool,
		onClose: PropTypes.func.isRequired,
		onCancel: PropTypes.func.isRequired,
		event: PropTypes.object,
		onOpenVisit: PropTypes.func,
		isOwner: PropTypes.bool,
		userId: PropTypes.number,
		owner: PropTypes.object,
		onUpdateEvent: PropTypes.func,
		onScheduleAppointment: PropTypes.func.isRequired,
		timeZone: PropTypes.string.isRequired,
		onScheduleAppointmentForNewPatient: PropTypes.func.isRequired,
		onScheduleTAV: PropTypes.func.isRequired,
	};

	state = {
		isLoadingEvisit: false,
		eventNotes: '',
		reasonsForVisit: [],
	};

	async componentDidMount() {
		const reasonsForVisit = [{ value: 'none', label: 'Choose...' }];
		reasonsForVisit.push(
			...(await getReasonsForVisit(this.props.userId, this.props.userId))
		);
		this.setState({ reasonsForVisit });
	}

	async componentDidUpdate(prevProps) {
		if (!prevProps.isOpen && this.props.isOpen) {
			this.setState({
				eventNotes: this.props.event.notes || '',
				showScheduleExistingPatientForm: false,
				virtualPractices: null,
			});
			if (this.props.event.evisit_id) {
				this.setState({
					isLoadingEvisit: true,
					evisit: undefined,
				});
				try {
					const evisit = await getEvisitDetails(this.props.event.evisit_id);
					if (!this.props.event.patient_id) {
						evisit.preRegPatient = await getPreRegPatient(
							this.props.userId,
							this.props.event.id
						);
					}
					this.setState({
						isLoadingEvisit: false,
						evisit,
					});
				} catch (e) {
					this.setState({
						isLoadingEvisit: false,
						isError: true,
					});
				}
			}
		}
	}

	handleEventNoteChange = ({ target }) => {
		this.setState({
			eventNotes: target.value,
		});
	};

	handleSaveEventNotes = () => {
		this.props.onUpdateEvent({ notes: this.state.eventNotes });
	};

	handleCancelOrDelete = () => {
		this.props.onCancel(this.state.evisit);
	};

	handleExistingPatientClick = async () => {
		const { data } = await getLinkedVirtualPractices(this.props.userId);
		if (data && data.status && data.status.result) {
			this.setState({ virtualPractices: data.status.result });
		}
		const practicePatients = await getPracticePatients(this.props.userId);
		const { reasonsForVisit } = this.state;
		this.setState({
			showScheduleExistingPatientForm: true,
			selectedReasonForVisit: reasonsForVisit[0].value,
			practicePatients,
			patientSearchValue: '',
			patientSuggestions: [],
			selectedPatient: undefined,
		});
	};

	handleNewPatientClick = () => {
		this.props.onScheduleAppointmentForNewPatient();
	};

	handleChangeAppointmentWith = ({ target }) => {
		this.setState({ selectedProviderId: target.value });
	};

	handleReasonForVisitSelected = ({ target }) => {
		this.setState({ selectedReasonForVisit: target.value });
	};

	handlePatientSuggestionsFetchRequested = ({ value }) => {
		const { practicePatients } = this.state;
		const upperValue = `${value}`.toUpperCase();
		const patientSuggestions = practicePatients.filter(
			({ patient_name_last, patient_name_first }) =>
				patient_name_last.toUpperCase().includes(upperValue) ||
				patient_name_first.toUpperCase().includes(upperValue)
		);
		this.setState({ patientSuggestions });
	};

	handlePracticeChange = async ({ target }) => {
		const selectedPractice = parseInt(target.value);
		const practicePatients = await getPracticePatients(
			this.props.userId,
			selectedPractice > 0 ? selectedPractice : null
		);
		this.setState({
			selectedPractice,
			practicePatients,
			patientSearchValue: '',
			patientSuggestions: [],
			selectedPatient: undefined,
		});
	};

	handlePatientSelected = (event, { suggestion }) => {
		event.preventDefault();
		this.setState({
			selectedPatient: suggestion,
		});
	};

	handlePatientSearchChange = (event, { newValue }) => {
		this.setState({ patientSearchValue: newValue });
	};

	isAppointmentInvalid = () => {
		const { reasonsForVisit, selectedReasonForVisit, selectedPatient } =
			this.state;
		return (
			selectedReasonForVisit === reasonsForVisit[0].value || !selectedPatient
		);
	};

	handleScheduleAppointment = () => {
		const { selectedPatient, selectedReasonForVisit } = this.state;
		const { event } = this.props;

		const evisit = {
			patientId: selectedPatient.patientId,
			providerId: event.provider_id,
			appointmentId: event.id,
			visitReason: selectedReasonForVisit,
			patientDependentId: selectedPatient.dependent_id,
		};
		this.props.onScheduleAppointment(evisit);
	};

	render() {
		const {
			onClose,
			isOpen,
			event = {},
			isOwner,
			onOpenVisit,
			userId,
			owner,
			timeZone,
			onScheduleTAV,
		} = this.props;
		const {
			isLoadingEvisit,
			evisit = {},
			eventNotes,
			showScheduleExistingPatientForm,
			reasonsForVisit,
			selectedReasonForVisit,
			patientSuggestions = [],
			patientSearchValue = '',
			virtualPractices,
			selectedPractice = -1,
		} = this.state;
		let docName;
		let currentProvider;
		let owningPractice;

		if (evisit && evisit.evisit_provider_transactions) {
			if (evisit.evisit_provider_transactions.length > 0) {
				currentProvider = evisit.evisit_provider_transactions[0].provider;
			} else {
				currentProvider = evisit.provider;
			}
		}

		if (currentProvider && userId != currentProvider.id) {
			docName = `${currentProvider.name_first} ${currentProvider.name_last}, ${currentProvider.credentials}`;
		}

		let practiceOptions;
		if (virtualPractices) {
			practiceOptions = [{ value: -1, label: 'My Practice' }];
			virtualPractices.forEach((practice) => {
				practiceOptions.push({
					value: practice.provider_practice_virtual_id,
					label: practice.practice_name,
				});
			});
		}

		if (event && event.practice_name) {
			owningPractice = event.practice_name;
			if (event.provider_id !== event.owning_provider_practice_id) {
				owningPractice = event.owning_practice_name;
			}
		}

		return (
			<Modal isOpen={isOpen} onRequestClose={onClose} className="modal-dialog">
				<div className="modal-content">
					<div className="modal-header">
						<button
							type="button"
							data-dismiss="modal"
							aria-label="Close"
							className="close"
							onClick={onClose}
						>
							<span aria-hidden="true">×</span>
						</button>
						<h4 className="modal-title">
							Time Slot -{' '}
							{event?.scheduled_start_dttm &&
								formatInTimeZone(
									new Date(event.scheduled_start_dttm),
									timeZone,
									'PPp'
								)}
						</h4>
					</div>
					<div className="modal-body form-horizontal">
						<fieldset>
							<legend>Time Slot Details</legend>
							{!isOwner && owner && (
								<div className="form-group">
									<label className="col-md-6 control-label">Calendar Of</label>
									<div className="col-md-6">
										<p className="form-control-static">
											{owner.name_first} {owner.name_last}, {owner.credentials}
										</p>
									</div>
								</div>
							)}
							<div className="form-group">
								<label className="col-md-6 control-label">Availability</label>
								<div className="col-md-6">
									<p className="form-control-static">
										{Boolean(event.available_ind) && 'Available'}
										{!event.available_ind && event.evisit_id && 'Scheduled'}
									</p>
								</div>
							</div>
							<div className="form-group">
								<label className="col-md-6 control-label">
									Start Date/Time
								</label>
								<div className="col-md-6">
									<p className="form-control-static">
										{event?.scheduled_start_dttm &&
											formatInTimeZone(
												new Date(event.scheduled_start_dttm),
												timeZone,
												'PPPPp'
											)}
									</p>
								</div>
							</div>
							<div className="form-group">
								<label className="col-md-6 control-label">Duration</label>
								<div className="col-md-6">
									<p className="form-control-static">
										{event.appointment_duration} minutes
									</p>
								</div>
							</div>
							<div className="form-group">
								<label className="col-md-6 control-label">Notes</label>
								<div className="col-md-6">
									{isOwner && !showScheduleExistingPatientForm ? (
										<textarea
											className="form-control"
											value={eventNotes}
											onChange={this.handleEventNoteChange}
										/>
									) : (
										<p className="form-control-static">
											{event.notes ? event.notes : '<None Entered>'}
										</p>
									)}
								</div>
							</div>
						</fieldset>
						{!event.available_ind && event.evisit_id && (
							<fieldset
								className={cn({
									whirl: isLoadingEvisit,
									traditional: isLoadingEvisit,
								})}
							>
								<legend>
									Visit Details
									{evisit.preRegPatient && <small> (Pre-registration)</small>}
								</legend>
								<div className="form-group">
									<label className="col-md-6 control-label">Patient</label>
									<div className="col-md-6">
										<p className="form-control-static">
											{evisit.patient_dependent &&
												`${evisit.patient_dependent.dependent_name} ${evisit.patient_dependent.dependent_name_last} `}
											{evisit.patient_dependent &&
												evisit.patient_dependent.dependent_gender &&
												`(${evisit.patient_dependent.dependent_gender})`}
											{!evisit.patient_dependent &&
												evisit.patient &&
												`${evisit.patient.name_first} ${evisit.patient.name_last} `}
											{!evisit.patient_dependent &&
												evisit.patient &&
												evisit.patient.gender &&
												`(${evisit.patient.gender})`}
											{evisit.preRegPatient &&
												`${evisit.preRegPatient.name_first} ${evisit.preRegPatient.name_last} `}
										</p>
									</div>
								</div>
								<div className="form-group">
									<label className="col-md-6 control-label">DOB</label>
									<div className="col-md-6">
										<p className="form-control-static">
											{evisit.patient_dependent &&
												short(
													evisit.patient_dependent.dependent_birth_dttm,
													true
												)}
											{!evisit.patient_dependent &&
												evisit.patient &&
												short(evisit.patient.birth_dttm, true)}
											{!evisit.patient_dependent &&
												evisit.preRegPatient &&
												formatInTimeZone(
													new Date(evisit.preRegPatient.birth_dt),
													timeZone,
													'P'
												)}
										</p>
									</div>
								</div>
								{evisit.preRegPatient && (
									<div className="form-group">
										<label className="col-md-6 control-label">
											Registration Code
										</label>
										<div className="col-md-6">
											<pre>
												{`${evisit.preRegPatient.confirmation_code.substring(
													0,
													3
												)}-${evisit.preRegPatient.confirmation_code.substring(
													3,
													6
												)}-${evisit.preRegPatient.confirmation_code.substring(
													6
												)}`}
											</pre>
										</div>
									</div>
								)}
								<div className="form-group">
									<label className="col-md-6 control-label">
										Reason for Visit
									</label>
									<div className="col-md-6">
										<p className="form-control-static">
											{evisit.provider_visit_type &&
												evisit.provider_visit_type.visit_type}
										</p>
									</div>
								</div>
								{(docName || owningPractice) && (
									<div className="form-group">
										<label className="col-md-6 control-label">Patient Of</label>
										<div className="col-md-6">
											<p className="form-control-static">
												{docName && (
													<React.Fragment>
														{docName}
														<br />
													</React.Fragment>
												)}
												{owningPractice}
											</p>
										</div>
									</div>
								)}
								{evisit.patient && (
									<div className="form-group">
										<label className="col-md-6 control-label">
											Patient Comments
										</label>
										<div className="col-md-6">
											<p className="form-control-static">
												{evisit.comments ? evisit.comments : '<None Entered>'}
											</p>
										</div>
									</div>
								)}
							</fieldset>
						)}
						{Boolean(event.available_ind) && !event.evisit_id && (
							<fieldset>
								<legend>Schedule Appointment with Patient</legend>
								{showScheduleExistingPatientForm ? (
									<React.Fragment>
										<div className="form-group">
											<label className="col-md-6 control-label">
												Appointment With
											</label>
											<div className="col-md-6">
												<p className="form-control-static">
													{owner.name_first} {owner.name_last},{' '}
													{owner.credentials}
												</p>
											</div>
										</div>
										{virtualPractices && (
											<div className="form-group">
												<label className="col-md-6 control-label">
													Patient from
												</label>
												<div className="col-md-6">
													<SelectRaw
														selectedValue={selectedPractice}
														options={practiceOptions}
														onChange={this.handlePracticeChange}
													/>
												</div>
											</div>
										)}
										<div className="form-group">
											<label className="col-md-6 control-label">Patient</label>
											<AutoSuggest
												suggestions={patientSuggestions}
												onSuggestionsFetchRequested={
													this.handlePatientSuggestionsFetchRequested
												}
												onSuggestionsClearRequested={() =>
													this.setState({ patientSuggestions: [] })
												}
												onSuggestionSelected={this.handlePatientSelected}
												getSuggestionValue={(patient) =>
													`${patient.patient_name_last}, ${patient.patient_name_first}`
												}
												renderSuggestion={PatientSuggestion}
												inputProps={{
													placeholder: 'Type to choose patient',
													onChange: this.handlePatientSearchChange,
													value: patientSearchValue,
												}}
												theme={{
													container: 'col-md-6',
													suggestionsContainerOpen: 'open',
													input: 'form-control',
													suggestionsList: 'dropdown-menu ml-lg mr-lg',
												}}
											/>
										</div>
										<div className="form-group">
											<label className="col-md-6 control-label">
												Reason for Visit
											</label>
											<div className="col-md-6">
												<SelectRaw
													selectedValue={selectedReasonForVisit}
													options={reasonsForVisit}
													onChange={this.handleReasonForVisitSelected}
												/>
											</div>
										</div>
									</React.Fragment>
								) : (
									<React.Fragment>
										<div className="col-md-4 text-center">
											<Button
												title="Existing Patient"
												onClick={this.handleExistingPatientClick}
												primary
											/>
										</div>
										<div className="col-md-4 text-center">
											<Button
												title="Text-a-Visit"
												onClick={onScheduleTAV}
												primary
												rightIcon="chevron-right"
											/>
										</div>
										<div className="col-md-4 text-center">
											<Button
												title="New Patient"
												onClick={this.handleNewPatientClick}
												primary
												rightIcon="chevron-right"
											/>
										</div>
									</React.Fragment>
								)}
							</fieldset>
						)}
					</div>
					<div className="modal-footer">
						<div className="pull-left">
							{isOwner &&
								!showScheduleExistingPatientForm &&
								!(!event.patient_id && event.evisit_id) && (
									<Button
										title={
											!event.available_ind && event.evisit_id
												? 'Cancel Appointment'
												: 'Delete Slot'
										}
										danger
										onClick={this.handleCancelOrDelete}
									/>
								)}
						</div>
						{showScheduleExistingPatientForm ? (
							<React.Fragment>
								<Button title="Cancel" onClick={onClose} />
								<Button
									primary
									title="Schedule Appointment"
									disabled={this.isAppointmentInvalid()}
									onClick={this.handleScheduleAppointment}
								/>
							</React.Fragment>
						) : (
							<React.Fragment>
								<Button title="Close" onClick={onClose} />
								{isOwner && (
									<Button
										title="Save &amp; Close"
										primary
										onClick={this.handleSaveEventNotes}
									/>
								)}
								{event.evisit_id && event.patient_id && (
									<Button title="View Visit" info onClick={onOpenVisit} />
								)}
							</React.Fragment>
						)}
					</div>
				</div>
			</Modal>
		);
	}
}
